import { ReactNode, useMemo } from 'react';

import { ExpandedState } from '@tanstack/react-table';

import {
  SectionLoader,
  SectionPaginationApi,
  useSectionRegistry,
} from '@/components/data-table/nested-pagination';

import { getApprovalMembers, getApprovalWorklogEntries } from '../api/time-tracking.api';
import { APPROVALS_QUERY_KEYS } from '../constants/approvals-query-keys';
import { GetApprovalFilterRequest } from '../types/dto/worklogs-approval.dto';
import {
  ApprovalEntryRow,
  ApprovalMemberRow,
  ApprovalProjectRow,
} from '../types/time-tracking-approvals';

const TAG = 'ApprovalsNestedPagination:';

const getExpandedIds = (expanded: ExpandedState): string[] => {
  if (typeof expanded === 'boolean') return [];
  return Object.entries(expanded)
    .filter(([, v]) => v)
    .map(([k]) => k);
};

interface UseLazyApprovalDataParams {
  projects: ApprovalProjectRow[];
  expanded: ExpandedState;
  filter: GetApprovalFilterRequest;
}

interface UseLazyApprovalDataResult {
  enrichedData: ApprovalProjectRow[];
  loadingRowIds: Set<string>;
  sectionPagination: SectionPaginationApi;
  /** Headless loader elements; render them in the table's JSX (they render null). */
  sectionLoaders: ReactNode;
}

/**
 * Lazy-loads + paginates the approvals table's nested levels. Composes the generic
 * nested-pagination machinery: one section registry per paginating level (members under
 * a project, worklogs under a member), each fed by a `SectionLoader`. The domain owns
 * only the parent→child seam (members carry the project context worklogs need), the
 * tree-overlay enrichment, and the combined pagination API.
 */
export const useLazyApprovalData = ({
  projects,
  expanded,
  filter,
}: UseLazyApprovalDataParams): UseLazyApprovalDataResult => {
  const expandedIds = getExpandedIds(expanded);
  const filterKey = JSON.stringify(filter);

  // One registry per paginating level. Resetting on filter change is handled internally.
  const members = useSectionRegistry<ApprovalMemberRow>({ resetKey: filterKey, logTag: TAG });
  const worklogs = useSectionRegistry<ApprovalEntryRow>({ resetKey: filterKey, logTag: TAG });

  // Expanded projects that need a member loader.
  const expandedProjects = useMemo(
    () => projects.filter(p => expandedIds.includes(p.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, expandedIds.join(',')]
  );

  // Expanded members (only those whose parent project's members page is loaded). Each
  // pair carries its `project` so the worklog loader can build the grandparent context.
  const expandedMembers = useMemo(() => {
    const result: Array<{ member: ApprovalMemberRow; project: ApprovalProjectRow }> = [];
    for (const project of expandedProjects) {
      const loadedMembers = members.getRows(project.id) ?? [];
      for (const member of loadedMembers) {
        if (expandedIds.includes(member.id)) {
          result.push({ member, project });
        }
      }
    }
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedProjects, expandedIds.join(','), members.version, members.getRows]);

  // Overlay loaded children onto the project tree. Stable-ref: only build new objects for
  // rows that actually gained children, so untouched subtrees keep identity.
  const enrichedData = useMemo(
    () =>
      projects.map(project => {
        const loadedMembers = members.getRows(project.id);
        if (!loadedMembers) return project;

        const enrichedMembers = loadedMembers.map(member => {
          const loadedWorklogs = worklogs.getRows(member.id);
          return loadedWorklogs ? { ...member, timeEntries: loadedWorklogs } : member;
        });

        return { ...project, members: enrichedMembers };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, members.version, worklogs.version, members.getRows, worklogs.getRows]
  );

  // Combined pagination across both levels. Correct because member and worklog section ids
  // are globally disjoint (`project-{id}` vs `project-{pid}-member-{mid}`), so first-match
  // resolution is unambiguous.
  const sectionPagination = useMemo<SectionPaginationApi>(
    () => ({
      getSection: id =>
        members.sectionPagination.getSection(id) ?? worklogs.sectionPagination.getSection(id),
      fetchNextPage: id => {
        if (members.sectionPagination.getSection(id)) {
          members.sectionPagination.fetchNextPage(id);
        } else {
          worklogs.sectionPagination.fetchNextPage(id);
        }
      },
    }),
    [members.sectionPagination, worklogs.sectionPagination]
  );

  const loadingRowIds = useMemo(
    () => new Set([...members.loadingSectionIds, ...worklogs.loadingSectionIds]),
    [members.loadingSectionIds, worklogs.loadingSectionIds]
  );

  // Headless loaders — one per expanded section. React mounts/unmounts them as sections
  // expand/collapse, giving each its own independent useInfiniteQuery instance.
  const sectionLoaders = (
    <>
      {expandedProjects.map(project => (
        <SectionLoader<ApprovalMemberRow>
          key={`member-loader-${project.id}`}
          sectionId={project.id}
          queryKey={APPROVALS_QUERY_KEYS.members(project.project.projectId, filter)}
          queryFn={cursor => getApprovalMembers(filter, project.project.projectId, cursor)}
          getRowId={member => member.member.id}
          onChange={members.onSectionChange}
          onUnmount={members.onSectionUnmount}
        />
      ))}
      {expandedMembers.map(({ member, project }) => (
        <SectionLoader<ApprovalEntryRow>
          key={`worklog-loader-${member.id}`}
          sectionId={member.id}
          queryKey={APPROVALS_QUERY_KEYS.worklogs(
            project.project.projectId,
            member.member.id,
            filter
          )}
          queryFn={cursor =>
            getApprovalWorklogEntries(
              filter,
              {
                projectId: project.project.projectId,
                projectName: project.project.projectName,
                projectColorHex: project.project.colorHex,
                projectHash: project.hash,
              },
              {
                memberId: member.member.id,
                memberName: member.member.name,
                memberAvatarUrl: member.member.avatarUrl,
                memberHash: member.hash,
              },
              cursor
            )
          }
          getRowId={entry => entry.id}
          onChange={worklogs.onSectionChange}
          onUnmount={worklogs.onSectionUnmount}
        />
      ))}
    </>
  );

  return { enrichedData, loadingRowIds, sectionPagination, sectionLoaders };
};
